import styled, { keyframes, createGlobalStyle, css } from 'styled-components';

// 动画定义
export const matchAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.8;
  }
  100% {
    transform: scale(0);
    opacity: 0;
  }
`;

// 新数字滑入动画
export const slideInAnimation = keyframes`
  0% {
    transform: scale(0);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

// 数字递增动画
export const incrementAnimation = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.3);
  }
  100% {
    transform: scale(1);
  }
`;

// 移动到目标点的动画 - 动态创建
const moveToTargetAnimation = (targetRow: number, targetCol: number) => keyframes`
  0% {
    transform: translate(0, 0) scale(1);
  }
  100% {
    transform: translate(
      calc((${targetCol} - var(--col)) * 100%),
      calc((${targetRow} - var(--row)) * 100%)
    ) scale(0.5);
    opacity: 0.7;
  }
`;

export const scorePopupAnimation = keyframes`
  0% {
    transform: translateY(0);
    opacity: 1;
  }
  100% {
    transform: translateY(-30px);
    opacity: 0;
  }
`;

// 淡入动画
export const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

// 道具消息动画
export const itemMessageAnimation = keyframes`
  0% {
    transform: translateY(10px);
    opacity: 0;
  }
  20% {
    transform: translateY(0);
    opacity: 1;
  }
  80% {
    transform: translateY(0);
    opacity: 1;
  }
  100% {
    transform: translateY(-10px);
    opacity: 0;
  }
`;

// 道具选择动画
export const itemSelectAnimation = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
    box-shadow: 0 0 10px rgba(78, 205, 196, 0.8);
  }
  100% {
    transform: scale(1);
  }
`;

// 全局样式
export const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
    font-family: 'Arial', sans-serif;
    // background-color: #f5f5f5;
    // background-color: #EDE6DB;
    background: #F0EAD6;
    // background: #EFEFEF;
    overscroll-behavior: none;
    touch-action: manipulation;
    -webkit-overflow-scrolling: touch;
    -webkit-tap-highlight-color: transparent;
  }
  *, *::before, *::after {
    box-sizing: border-box;
  }
`;

// 组件样式
export const GameContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 5px;
  width: 100%;
  box-sizing: border-box;
  max-width: 100vw;
  overflow-x: hidden;
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-gap: 8px;
  background-color: #e0e0e0;
  padding: 10px;
  border-radius: 10px;
  // margin-top: 20px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

export const Cell = styled.div<{ 
  color: string; 
  $isMatched?: boolean; 
  $isNew?: boolean; 
  $isIncremented?: boolean;
  $isMovingToTarget?: boolean;
  $isReviving: boolean;
  $targetRow?: number;
  $targetCol?: number;
  $isSelected?: boolean;
}>`
  width: calc(min(65px, 16.5vw));
  height: calc(min(65px, 16.5vw));
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.color};
  border-radius: 8px;
  font-size: calc(min(22px, 7vw));
  color: white;
  cursor: pointer;
  transition: transform 0.2s;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  position: relative;
  --row: 0;
  --col: 0;
  
  ${props => {
    if (props.$isMovingToTarget && typeof props.$targetRow === 'number' && typeof props.$targetCol === 'number') {
      return css`
        animation: ${moveToTargetAnimation(props.$targetRow, props.$targetCol)} 0.3s ease-in-out forwards;
      `;
    } else if (props.$isMatched) {
      return css`
        animation: ${matchAnimation} 0.3s ease-in-out;
      `;
    } else if (props.$isNew) {
      return css`
        animation: ${slideInAnimation} 0.3s ease-in-out;
      `;
    } else if (props.$isIncremented) {
      return css`
        animation: ${incrementAnimation} 0.3s ease-in-out;
      `;
    }
    return '';
  }}
  
  ${props => props.$isReviving && css`
    animation: revive 3s ease-in-out;
    box-shadow: 0 0 20px ${props.color};
  `}

  @keyframes revive {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    20% {
      transform: scale(1.2);
      opacity: 0.8;
    }
    40% {
      transform: scale(0.8);
      opacity: 0.6;
    }
    60% {
      transform: scale(1.1);
      opacity: 0.8;
    }
    80% {
      transform: scale(0.9);
      opacity: 0.9;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
  
  will-change: transform, opacity;

  &:hover {
    transform: scale(1.05);
  }
  
  ${props => props.$isSelected && css`
    box-shadow: 0 0 0 4px rgba(255, 255, 0, 0.7);
  `}
`;

export const ScorePopupText = styled.div`
  position: fixed;
  transform: translate(-50%, -100%);
  color: #FFC107;
  font-size: 20px;
  font-weight: bold;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  animation: ${scorePopupAnimation} 1s ease-out forwards;
  pointer-events: none;
  z-index: 10;
`;

// 游戏信息展示面板
export const StatsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  max-width: 350px;
  font-size: 18px;
  margin: 5px 0;
  color: #555;
  font-weight: bold;
  background-color: white;
  padding: 10px 15px;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

export const SoundToggle = styled.div`
  display: flex;
  align-items: center;
  font-size: 16px;
  color: #555;
  cursor: pointer;
  margin: 10px 0;
  padding: 5px 10px;
  border-radius: 20px;
  background-color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background-color: #f0f0f0;
  }
`;

export const SoundIcon = styled.span<{ $soundOn: boolean }>`
  margin-right: 8px;
  opacity: ${props => props.$soundOn ? 1 : 0.5};
  transition: opacity 0.2s;
`;

// 游戏说明样式
export const InstructionsButton = styled.button`
  background: #4ECDC4;
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 20px;
  margin: 10px 0;
  font-size: 16px;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
  
  &:hover {
    background: #3dbbad;
    transform: translateY(-2px);
  }
`;

export const InstructionsPanel = styled.div<{ $isVisible: boolean }>`
  max-height: ${props => props.$isVisible ? '400px' : '0'};
  opacity: ${props => props.$isVisible ? '1' : '0'};
  overflow: hidden;
  transition: all 0.3s ease-in-out;
  width: 100%;
  max-width: 500px;
  background-color: white;
  border-radius: 10px;
  margin-top: ${props => props.$isVisible ? '15px' : '0'};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

export const InstructionsContent = styled.div`
  padding: 15px;
  font-size: 14px;
  color: #333;
  line-height: 1.5;
`;

// 游戏结束界面样式
export const GameOverOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease-in-out;
`;

export const GameOverPanel = styled.div`
  width: 80%;
  max-width: 350px;
  background-color: white;
  border-radius: 15px;
  padding: 20px;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  animation: ${slideInAnimation} 0.3s ease-out;
`;

export const GameOverTitle = styled.h2`
  margin-top: 0;
  color: #FF6B6B;
  font-size: 24px;
`;

export const GameOverScore = styled.div`
  font-size: 20px;
  margin: 15px 0;
  color: #333;
`;

export const RestartButton = styled.button`
  background: #4ECDC4;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 25px;
  font-size: 16px;
  margin-top: 15px;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
  
  &:hover {
    background: #3dbbad;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
`;

// 道具系统样式
export const ItemsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin: 15px 0;
  width: 100%;
  max-width: 350px;
`;

export const ItemWrapper = styled.div<{ $isActive: boolean }>`
  position: relative;
  cursor: ${props => props.$isActive ? 'pointer' : 'not-allowed'};
  opacity: ${props => props.$isActive ? 1 : 0.5};
  transition: all 0.2s ease;
  
  &:hover {
    transform: ${props => props.$isActive ? 'translateY(-2px)' : 'none'};
  }
`;

export const ItemButton = styled.button<{ $isSelected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 10px;
  background-color: white;
  border: 2px solid #e0e0e0;
  font-size: 24px;
  padding: 5px;
  cursor: inherit;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  ${props => props.$isSelected && css`
    border-color: #4ECDC4;
    animation: ${itemSelectAnimation} 0.5s ease-in-out;
    box-shadow: 0 0 8px rgba(78, 205, 196, 0.5);
  `}
`;

export const ItemName = styled.div`
  font-size: 10px;
  margin-top: 2px;
  color: #555;
`;

export const ItemCount = styled.div`
  position: absolute;
  top: -5px;
  right: -5px;
  background-color: #FF6B6B;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
`;

export const ItemMessageContainer = styled.div`
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  width: 80%;
  max-width: 300px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  pointer-events: none;
`;

export const ItemMessageText = styled.div`
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 10px 15px;
  border-radius: 20px;
  font-size: 16px;
  animation: ${itemMessageAnimation} 3s forwards;
  text-align: center;
`;

export const ItemDescriptionPanel = styled.div`
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 10px;
  border-radius: 5px;
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 10px;
  width: max-content;
  max-width: 150px;
  font-size: 12px;
  z-index: 100;
  visibility: hidden;
  opacity: 0;
  transition: visibility 0s, opacity 0.3s;
  
  ${ItemWrapper}:hover & {
    visibility: visible;
    opacity: 1;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: rgba(0, 0, 0, 0.8) transparent transparent transparent;
  }
`;

// 商店按钮
export const ShopButton = styled.button`
  position: fixed;
  top: 10px;
  right: 10px;
  background-color: #FFD700;
  border: none;
  border-radius: 8px;
  color: #222;
  padding: 8px 15px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 10;
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

// 商店图标
export const ShopIcon = styled.span`
  font-size: 18px;
`;

// 钻石显示-商店
export const DiamondDisplay = styled.div`
  font-size: 16px;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 15px;
  & > span {
    color: #1E90FF;
  }
`;

// 钻石图标
export const DiamondIcon = styled.span`
  color: #1E90FF;
  font-size: 18px;
`;

// 商店弹窗覆盖层
export const ShopOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
  animation: ${fadeIn} 0.3s ease-in-out;
`;

// 商店面板
export const ShopPanel = styled.div`
  background-color: #fff;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  position: relative;
`;

// 商店标题区
export const ShopHeader = styled.div`
  padding: 20px 20px 0 20px;
  position: sticky;
  top: 0;
  background-color: #fff;
  border-radius: 12px 12px 0 0;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    padding: 15px 15px 0 15px;
  }
`;

// 商店标题
export const ShopTitle = styled.h2`
  text-align: center;
  margin: 0 0 20px 0;
  color: #333;
  font-size: 24px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background-color: #FFD700;
    border-radius: 3px;
  }
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

// 商店关闭按钮
export const ShopCloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  color: #777;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  
  &:hover {
    color: #333;
    background-color: rgba(0, 0, 0, 0.05);
  }
  
  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
    top: 10px;
    right: 10px;
  }
`;

// 商品列表容器
export const ShopItemsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
  margin: 10px 0;
  padding: 0 20px;
  overflow-y: auto;
  max-height: calc(90vh - 180px);
  scrollbar-width: thin;
  scrollbar-color: #ccc #f5f5f5;
  
  /* 自定义滚动条 */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f5f5f5;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: #ccc;
    border-radius: 4px;
    border: 2px solid #f5f5f5;
  }
  
  /* 在移动设备上的样式优化 */
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    max-height: calc(90vh - 160px);
    padding: 0 10px;
  }
`;

// 商品项
export const ShopItemCard = styled.div`
  background-color: #f8f8f8;
  border-radius: 8px;
  padding: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: transform 0.2s, box-shadow 0.2s;
  border: 1px solid #e0e0e0;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
  }
  
  /* 移动端样式优化 */
  @media (max-width: 768px) {
    padding: 10px;
  }
`;

// 商品图标
export const ShopItemIcon = styled.div`
  font-size: 32px;
  margin-bottom: 10px;
  
  @media (max-width: 768px) {
    font-size: 28px;
    margin-bottom: 8px;
  }
`;

// 商品名称
export const ShopItemName = styled.div`
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 5px;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

// 商品描述
export const ShopItemDescription = styled.div`
  font-size: 12px;
  color: #666;
  text-align: center;
  margin-bottom: 10px;
  height: 36px;
  overflow: hidden;
  
  @media (max-width: 768px) {
    font-size: 11px;
    height: 33px;
    margin-bottom: 8px;
  }
`;

// 商品价格
export const ShopItemPrice = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-weight: bold;
  color: #1E90FF;
  margin-bottom: 10px;
  
  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 8px;
  }
`;

// 数量选择器容器
export const QuantitySelector = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 5px;
  
  @media (max-width: 768px) {
    gap: 8px;
  }
`;

// 数量按钮
export const QuantityButton = styled.button<{ $increase?: boolean }>`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: none;
  background-color: ${props => props.$increase ? '#4CAF50' : '#F44336'};
  color: white;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${props => props.$increase ? '#3e8e41' : '#d32f2f'};
  }
  
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    width: 26px;
    height: 26px;
    font-size: 16px;
  }
`;

// 数量显示
export const QuantityDisplay = styled.span`
  font-size: 18px;
  font-weight: bold;
  min-width: 20px;
  text-align: center;
`;

// 购物车总结
export const CartSummary = styled.div`
  padding: 15px 20px;
  border-top: 1px solid #e0e0e0;
  background-color: #fcfcfc;
  position: sticky;
  bottom: 0;
  border-radius: 0 0 12px 12px;
  z-index: 10;
`;

// 总计
export const TotalPrice = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 15px;
  
  span:last-child {
    color: #1E90FF;
    display: flex;
    align-items: center;
    gap: 5px;
  }
`;

// 购买按钮
export const BuyButton = styled.button<{ $enabled: boolean }>`
  background-color: ${props => props.$enabled ? '#4CAF50' : '#cccccc'};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px;
  font-size: 16px;
  font-weight: bold;
  cursor: ${props => props.$enabled ? 'pointer' : 'not-allowed'};
  width: 100%;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${props => props.$enabled ? '#3e8e41' : '#cccccc'};
  }
`;

// 商店消息
export const ShopMessageContainer = styled.div`
  position: fixed;
  top: 60px;
  right: 10px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

// 商店消息项
export const ShopMessageItem = styled.div<{ $type: 'success' | 'error' | 'info' }>`
  background-color: ${props => {
    switch(props.$type) {
      case 'success': return '#4CAF50';
      case 'error': return '#F44336';
      case 'info': 
      default: return '#2196F3';
    }
  }};
  color: white;
  border-radius: 8px;
  padding: 10px 15px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  animation: ${itemMessageAnimation} 3s ease-in-out forwards;
  max-width: 300px;
`;

// 充值按钮
export const RechargeButton = styled.button`
  background-color: #FF9800;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px;
  width: 100%;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  margin-top: 10px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #F57C00;
  }
`;

// 游戏模式切换按钮
export const ModeToggle = styled.button`
  background: #FF6B6B;
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 20px;
  margin: 10px 0;
  font-size: 16px;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
  
  &:hover {
    background: #e55c5c;
    transform: translateY(-2px);
  }
`;

// 积分显示容器
export const ScoreDisplayContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  width: 100%;
  max-width: 350px;
  margin: 10px 0;
`;

export const ScoreDisplayItem = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 16px;
  color: #555;
  background-color: white;
  padding: 10px 15px;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  span:first-child {
    color: #666;
  }
  
  span:last-child {
    font-weight: bold;
    font-size: 24px;
    color: #333;
  }
`;

// 关卡显示
export const LevelDisplay = styled.div`
  background-color: #4ECDC4;
  color: white;
  padding: 8px 15px;
  border-radius: 20px;
  font-size: 16px;
  font-weight: bold;
  margin: 5px 0;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-width: 350px;
  width: 100%;
`;

export const LevelGoalDisplay = styled.div`
  background-color: #FFD166;
  color: #333;
  padding: 8px 15px;
  border-radius: 20px;
  font-size: 14px;
  margin: 5px 0 10px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-width: 350px;
  width: 100%;
`;

// 关卡完成弹窗
export const LevelCompleteOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease-in-out;
`;

export const LevelCompletePanel = styled.div`
  width: 80%;
  max-width: 350px;
  background-color: white;
  border-radius: 15px;
  padding: 20px;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  animation: ${slideInAnimation} 0.3s ease-out;
`;

export const LevelCompleteTitle = styled.h2`
  margin-top: 0;
  color: #4ECDC4;
  font-size: 24px;
`;

export const LevelCompleteMessage = styled.div`
  font-size: 18px;
  margin: 10px 0;
  color: #333;
`;

export const LevelRewards = styled.div`
  background-color: #f8f8f8;
  padding: 15px;
  border-radius: 10px;
  margin: 15px 0;
  text-align: left;
  
  div {
    margin: 5px 0;
    font-size: 16px;
    color: #555;
  }
`;

export const NextLevelButton = styled.button`
  background: #4ECDC4;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 25px;
  font-size: 16px;
  margin-top: 10px;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
  
  &:hover {
    background: #3dbbad;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
`;

// 游戏模式切换器（无需实际使用，但保留为Game.tsx导入）
export const GameModeSwitcher = styled.div``; 